import { getDomainStoryLines, uniqueStoryParagraphs } from "./careProfileStoryText";
import { CARE_PROFILE_SECTION_IDS } from "./careProfileSections";
import { buildProfileContractSafely } from "../features/profileContract";

const EMPTY_WORDS = new Set(["", "none", "not set", "not added"]);

const toLabel = (item) => {
  if (typeof item === "string") return item.trim();
  return String(item?.label || item?.name || item?.title || item?.value || "").trim();
};

const toTextArray = (value) => {
  const values = Array.isArray(value) ? value : [value];
  return values.map(toLabel).filter((item) => !EMPTY_WORDS.has(item.toLowerCase()));
};

const unique = (items) => Array.from(new Set(items.filter(Boolean)));

const formatList = (items) => {
  const values = unique(items);
  return values.join(" • ");
};

const getCareFocusItems = ({ selectedConditions = [], careContext = {} } = {}) => (
  unique([
    ...toTextArray(selectedConditions),
    ...toTextArray(careContext.careFocus),
    ...toTextArray(careContext.trackingFocus),
  ])
);

const getMedicationNames = (medicalProfile = {}) => {
  const medicationRecords = Array.isArray(medicalProfile.medicationRecords)
    ? medicalProfile.medicationRecords
      .filter((entry) => entry?.name && !entry.isArchived)
      .map((entry) => entry.name)
    : [];

  return medicationRecords;
};

const getDocumentNames = (uploadedDocuments = {}, key) => (
  Array.isArray(uploadedDocuments[key]) ? uploadedDocuments[key].map((item) => item?.name).filter(Boolean) : []
);

const getStoryText = (chapter) => [
  ...(chapter.paragraphs || []),
  ...(chapter.highlights || []).map((item) => `${item.label}: ${item.text}`),
  chapter.callout?.text || "",
  ...(chapter.contacts || []).map((item) => `${item.name}${item.detail ? ` - ${item.detail}` : ""}`),
].filter(Boolean).join("\n\n");

const getLensCopy = (lensKey, displayName) => {
  const name = displayName || "This person";
  const common = {
    title: `${name}'s Care Story`,
    subtitle: "An overview of saved care profile details. Update the Care Profile to change this story.",
    detailsHeading: "Care story sections",
  };

  const copyByLens = {
    autism: {
      ...common,
      fallback: `This story should help caregivers understand how ${name} communicates, regulates, and moves through the day.`,
      whoTitle: `Who ${name} is`,
      hardMomentsTitle: `${name}'s harder moments`,
      whatHelpsTitle: "What helps",
      sensoryTitle: `${name}'s sensory world`,
      rhythmTitle: "Daily rhythm",
      healthTitle: "Health & medications",
      emergencyTitle: "Emergency contacts",
    },
    dementia: {
      ...common,
      fallback: `This story should help caregivers understand ${name}'s familiar routines, reassurance needs, and safety context.`,
      whoTitle: `Who ${name} is`,
      hardMomentsTitle: "Distress signs & safety",
      whatHelpsTitle: "What reassures",
      sensoryTitle: "Comfort & environment",
      rhythmTitle: "Daily orientation",
      healthTitle: "Health & medications",
      emergencyTitle: "Emergency contacts",
    },
    chronic_illness: {
      ...common,
      fallback: `This story should help caregivers understand ${name}'s health baseline, warning signs, and daily support needs.`,
      whoTitle: `Who ${name} is`,
      hardMomentsTitle: "Symptoms & warning signs",
      whatHelpsTitle: "What helps on hard days",
      sensoryTitle: "Triggers & comfort needs",
      rhythmTitle: "Pacing & routines",
      healthTitle: "Health & medications",
      emergencyTitle: "Emergency contacts",
    },
    hospice: {
      ...common,
      fallback: `This story should help caregivers understand ${name}'s comfort needs, care wishes, and who to call.`,
      whoTitle: `Who ${name} is`,
      hardMomentsTitle: "Comfort & symptoms",
      whatHelpsTitle: "What brings comfort",
      sensoryTitle: "Comfort environment",
      rhythmTitle: "Daily comfort routine",
      healthTitle: "Medications & comfort instructions",
      emergencyTitle: "Who to call",
    },
  };

  return copyByLens[lensKey] || {
    ...common,
    fallback: `This story should help caregivers understand ${name}'s routines, support needs, and care context.`,
    whoTitle: `Who ${name} is`,
    hardMomentsTitle: "Harder moments",
    whatHelpsTitle: "What helps",
    sensoryTitle: "Comfort needs",
    rhythmTitle: "Daily rhythm",
    healthTitle: "Health & medications",
    emergencyTitle: "Emergency contacts",
  };
};

const buildChapter = ({
  id,
  title,
  sourceLabel,
  editSectionId,
  targetStep,
  tone,
  paragraphs = [],
  highlights = [],
  callout = null,
  contacts = [],
  emptyText = "",
}) => {
  const usableHighlights = highlights.filter((item) => item?.text);
  const usableParagraphs = uniqueStoryParagraphs(paragraphs);
  const usableContacts = contacts.filter((item) => item?.name || item?.detail);
  const usableCallout = callout?.text ? callout : null;

  return {
    id,
    title,
    sourceLabel,
    editSectionId,
    targetStep,
    tone,
    paragraphs: usableParagraphs,
    highlights: usableHighlights,
    callout: usableCallout,
    contacts: usableContacts,
    emptyText,
    hasContent: Boolean(
      usableParagraphs.length
      || usableHighlights.length
      || usableContacts.length
      || usableCallout
    ),
  };
};

const getSection = (profileOverview = {}, id) => {
  const sections = [
    ...(profileOverview.coreSections || []),
    ...(profileOverview.adaptiveGroup?.sections || []),
  ];
  return sections.find((section) => section.id === id) || {};
};

const getResolvedProfileContract = ({
  profileContract = null,
  selectedConditions = [],
  careContext = {},
  medicalProfile = {},
  supportProfile = {},
  knowledgeFacts = [],
  profileOverview = {},
} = {}) => {
  if (profileContract) return profileContract;

  const child = {
    conditions: selectedConditions,
    careContext,
    medicalProfile,
    supportProfile,
  };
  return buildProfileContractSafely({
    child,
    knowledgeFacts,
    careContext,
    ageGroup: profileOverview.adaptiveGroup?.key || careContext?.ageGroup || "",
  }).contract;
};

export const buildCareProfileStory = ({
  displayName = "",
  age = "",
  photoURL = "",
  selectedConditions = [],
  careContext = {},
  medicalProfile = {},
  supportProfile = {},
  uploadedDocuments = {},
  profileOverview = {},
  knowledgeFacts = [],
  profileContract = null,
} = {}) => {
  const name = String(displayName || "This person").trim();
  const lensKey = profileOverview.adaptiveGroup?.key || "general";
  const copy = getLensCopy(lensKey, name);
  const careFocusItems = getCareFocusItems({ selectedConditions, careContext });
  const resolvedProfileContract = getResolvedProfileContract({
    profileContract,
    selectedConditions,
    careContext,
    medicalProfile,
    supportProfile,
    knowledgeFacts,
    profileOverview,
  });
  const communicationDomain = resolvedProfileContract.communication || {};
  const whatHelpsDomain = resolvedProfileContract.whatHelpsComfort || {};
  const watchForDomain = resolvedProfileContract.watchFor || {};
  const dailyLifeDomain = resolvedProfileContract.dailyLifeRoutines || {};
  const healthCareDomain = resolvedProfileContract.healthCare || {};
  const medications = getMedicationNames(medicalProfile);
  const emergencyContacts = String(supportProfile.emergencyContacts || "").trim();
  const allergies = toTextArray(medicalProfile.foodAllergies);
  const dietary = toTextArray(medicalProfile.dietaryRestrictions);
  const communication = [
    ...toTextArray(supportProfile.communicationStyle),
    ...toTextArray(medicalProfile.communicationNeeds),
  ];
  const whatHelps = [
    ...toTextArray(supportProfile.whatHelps),
    ...toTextArray(supportProfile.thingsThatUsuallyHelp),
  ];
  const hardMoments = [
    ...toTextArray(medicalProfile.behavioralTriggers),
    ...toTextArray(supportProfile.whatCanBeDifficult),
  ];
  const whoTheyAreValues = Array.isArray(supportProfile.whoTheyAre)
    ? supportProfile.whoTheyAre
    : Object.values(supportProfile.whoTheyAre || {});
  const careGoals = Array.isArray(supportProfile.careGoals)
    ? supportProfile.careGoals
    : [supportProfile.careGoals].filter(Boolean);
  const whoTheyAre = toTextArray(whoTheyAreValues.flat());
  const warningSigns = toTextArray(supportProfile.earlyWarningSigns);
  const sensoryNeeds = toTextArray(medicalProfile.sensoryIssues);
  const goals = toTextArray(careGoals.map((goal) => goal?.title || goal?.description || goal));
  const familyTips = toTextArray(supportProfile.familySupportTips);
  const medicalDocuments = getDocumentNames(uploadedDocuments, "medical");
  const medicationDocuments = getDocumentNames(uploadedDocuments, "medications");
  const behavioralDocuments = getDocumentNames(uploadedDocuments, "behavioral");
  const safetyNotes = toTextArray(supportProfile.safetyConsiderations);
  const thingsToAvoid = toTextArray(supportProfile.thingsToAvoid);
  const emergencyContactLines = emergencyContacts
    .split(/\n+/)
    .map((item) => item.trim())
    .filter(Boolean);

  const aboutSection = getSection(profileOverview, CARE_PROFILE_SECTION_IDS.ABOUT);
  const supportSection = getSection(profileOverview, CARE_PROFILE_SECTION_IDS.DAILY_SUPPORT);
  const behaviorSection = getSection(profileOverview, CARE_PROFILE_SECTION_IDS.BEHAVIOR_SUPPORT);
  const sensorySection = getSection(profileOverview, CARE_PROFILE_SECTION_IDS.SENSORY_PROFILE);
  const routineSection = getSection(profileOverview, CARE_PROFILE_SECTION_IDS.ROUTINE_TRANSITIONS);
  const goalsSection = getSection(profileOverview, CARE_PROFILE_SECTION_IDS.GOALS);
  const medicationSection = getSection(profileOverview, CARE_PROFILE_SECTION_IDS.MEDICATION);

  const storyLead = supportProfile.about || "";

  const chapters = [
    buildChapter({
      id: "who",
      title: copy.whoTitle,
      sourceLabel: "About",
      editSectionId: CARE_PROFILE_SECTION_IDS.ABOUT,
      targetStep: aboutSection.targetStep,
      tone: "who",
      paragraphs: [
        storyLead,
        ...communication,
        ...getDomainStoryLines(communicationDomain),
        supportProfile.communicationNotes || "",
        ...whoTheyAre,
      ],
      emptyText: `Add what helps caregivers understand ${name} as a person.`,
    }),
    buildChapter({
      id: "hardMoments",
      title: copy.hardMomentsTitle,
      sourceLabel: "Behavior",
      editSectionId: CARE_PROFILE_SECTION_IDS.BEHAVIOR_SUPPORT,
      targetStep: behaviorSection.targetStep,
      tone: "hardMoments",
      paragraphs: [
        ...hardMoments,
        ...warningSigns,
        ...getDomainStoryLines(watchForDomain),
      ],
      callout: safetyNotes.length ? {
        label: "Safety",
        text: formatList(safetyNotes),
      } : null,
      highlights: [
        { label: "Support documents", text: formatList(behavioralDocuments) },
      ],
      emptyText: "Add triggers, early warning signs, or safety notes.",
    }),
    buildChapter({
      id: "whatHelps",
      title: copy.whatHelpsTitle,
      sourceLabel: "Daily Support",
      editSectionId: CARE_PROFILE_SECTION_IDS.DAILY_SUPPORT,
      targetStep: supportSection.targetStep,
      tone: "whatHelps",
      paragraphs: [
        ...whatHelps,
        ...thingsToAvoid.map((text) => `Things to avoid: ${text}`),
        ...getDomainStoryLines(whatHelpsDomain),
      ],
      emptyText: "Add practical supports, comfort tools, and things to avoid.",
    }),
    buildChapter({
      id: "sensory",
      title: copy.sensoryTitle,
      sourceLabel: "Behavior",
      editSectionId: CARE_PROFILE_SECTION_IDS.SENSORY_PROFILE,
      targetStep: sensorySection.targetStep || behaviorSection.targetStep,
      tone: "sensory",
      paragraphs: [
        ...sensoryNeeds,
      ],
      emptyText: "Add sensory needs, comfort tools, or environmental supports.",
    }),
    buildChapter({
      id: "rhythm",
      title: copy.rhythmTitle,
      sourceLabel: "Goals & Growth",
      editSectionId: CARE_PROFILE_SECTION_IDS.GOALS,
      targetStep: goalsSection.targetStep || routineSection.targetStep || supportSection.targetStep,
      tone: "rhythm",
      paragraphs: [
        ...familyTips,
        ...goals,
        supportProfile.schoolTherapySuggestions || "",
        ...getDomainStoryLines(dailyLifeDomain),
      ],
      emptyText: "Add routines, care priorities, planning notes, or ways family can help.",
    }),
    buildChapter({
      id: "health",
      title: copy.healthTitle,
      sourceLabel: "Medication",
      editSectionId: CARE_PROFILE_SECTION_IDS.MEDICATION,
      targetStep: medicationSection.targetStep,
      tone: "health",
      paragraphs: [
        ...getDomainStoryLines(healthCareDomain),
        medications.length ? `Medication details listed for review: ${formatList(medications)}.` : "",
        allergies.length ? `Food allergy: ${formatList(allergies)}.` : "",
        dietary.length ? `Dietary notes: ${formatList(dietary)}.` : "",
      ],
      highlights: [
        { label: "Care documents", text: formatList(medicalDocuments) },
        { label: "Medication documents", text: formatList(medicationDocuments) },
      ],
      emptyText: "Add medications, allergies, food notes, or care documents.",
    }),
    buildChapter({
      id: "emergency",
      title: copy.emergencyTitle,
      sourceLabel: "About",
      editSectionId: CARE_PROFILE_SECTION_IDS.ABOUT,
      targetStep: aboutSection.targetStep,
      tone: "emergency",
      paragraphs: emergencyContactLines.length ? [] : [
        emergencyContacts ? `Emergency contacts: ${emergencyContacts}` : "",
      ],
      contacts: emergencyContactLines.map((line) => {
        const [rawName, ...details] = line.split(":");
        return {
          name: rawName?.trim() || line,
          detail: details.join(":").trim(),
        };
      }),
      emptyText: "Add who to contact first, phone numbers, or important backup contact notes.",
    }),
  ];

  return {
    title: copy.title,
    subtitle: copy.subtitle,
    detailsHeading: copy.detailsHeading,
    profile: {
      displayName: name,
      age,
      photoURL,
      careFocus: formatList(careFocusItems),
    },
    summary: chapters.some((chapter) => chapter.hasContent)
      ? chapters.map((chapter) => getStoryText(chapter)).filter(Boolean)
      : [copy.fallback],
    shareTitle: `Share ${name}'s story`,
    shareActionLabel: "Copy story as text",
    chapters,
  };
};

export const buildCareProfileStoryShareText = (story = {}, selections = {}) => {
  const selectedChapters = (story.chapters || []).filter((chapter) => (
    selections[chapter.id] !== false && chapter.hasContent
  ));

  const text = [
    story.title || "Care Story",
    ...selectedChapters.flatMap((chapter) => [
      "",
      chapter.title,
      getStoryText(chapter),
    ]),
  ].filter((item) => item !== null && item !== undefined).join("\n");

  return {
    text,
    selectedChapters,
  };
};
